import { EuiFlexGroup, EuiFlexItem } from "@elastic/eui";
import React, { useEffect, useState } from "react";
import { useMapDataToUiWithFilter } from "hooks/mapDataToOptions";
import { ButtonRow } from "styles/elements/main";
import { Button, SelectWithSearch } from "components";
import FilterLabel from "modules/shared/profiles/filters/FilterLabel";
import {
    NATIONALITIES,
    PAYMENT_METHODS,
    QA_STATUS,
    SUPPLIER_TYPES,
} from "modules/shared/profiles/constants";
import trackAnalytics from "trackAnalytics";
import { LICENSE, PLATFORMS } from "../staticData";

const DriversFilters = ({ setDriversFilterOptions, filterOptions }) => {
    const {
        data: governorates,
        setSearchFilter: setGovernoratesFilter,
        clearSearchFilter: clearGovernoratesFilter,
    } = useMapDataToUiWithFilter("governorates");
    const [filters, setFilters] = useState({});
    const [totalFilters, setTotalFilters] = useState(0);

    const analyticsEvents = {
        nationality: "Filter_driver_by_nationality_clicked",
        workingGovernorates: "Filter_driver_by_governorate_clicked",
        supplierType: "Filter_driver_by_supplier_type_clicked",
        paymentMethodType: "Filter_driver_by_payment_method_clicked",
        qaStatus: "Filter_driver_by_status_clicked",
        source: "Filter_driver_by_platform_clicked",
        licenseType: "Filter_driver_by_license_clicked",
    };

    useEffect(() => {
        const sum = Object?.values(filters)?.reduce((acc, curr) => acc + curr?.value?.length, 0);
        setTotalFilters(sum);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filterOptions]);

    const handelClearAllFilters = () => {
        const updatedFilters = {};
        // eslint-disable-next-line no-restricted-syntax, guard-for-in
        for (const filter in filters) {
            updatedFilters[filter] = { value: [] };
        }
        setFilters(updatedFilters);
        setDriversFilterOptions([]);
    };

    const handleClearSingleFilter = (filterName) => {
        const filtered = { ...filterOptions[0] };
        delete filtered[filterName];
        setFilters((prev) => ({ ...prev, [filterName]: { value: [] } }));
        setDriversFilterOptions([filtered]);
    };

    const handleApplyFilter = (id, name, newFilters) => {
        const appliedOptions = [];
        let filterValues = [];
        const updatedFilters = newFilters || filters;
        // eslint-disable-next-line no-restricted-syntax
        for (const option in updatedFilters) {
            if (updatedFilters[option].id === id) {
                const filter = updatedFilters[option];
                filterValues = filter.value.map((element) => element.label);
                appliedOptions.push({ ...updatedFilters, [option]: filter });
            }
        }
        if (analyticsEvents[name]) {
            trackAnalytics(analyticsEvents[name], {
                "Filter Value": filterValues,
            });
        }
        setDriversFilterOptions(appliedOptions);
    };

    const handleChangedData = (value, name, id) => {
        const newFilters = {
            ...filters,
            [name]: { value, id },
        };
        setFilters(newFilters);
        // eslint-disable-next-line no-plusplus
        for (let i = 0; i < filterOptions?.length; i++) {
            if (newFilters[name]?.value?.length < filterOptions[i][name]?.value?.length) {
                handleApplyFilter(id, name, newFilters);
            }
        }
    };

    const constantsCustomSearchFilter = (filterType, value) => {
        return filterType.filter((item) => item.label.toLowerCase().includes(value.toLowerCase()));
    };

    const ActionBtns = ({ name, id }) => (
        <ButtonRow>
            <Button
                name="Clear"
                type="button"
                btnStyle="secondBtn"
                onChange={() => handleClearSingleFilter(name)}
            />
            <Button
                name="Apply"
                onChange={() => handleApplyFilter(id, name)}
                type="button"
                btnStyle="primaryBtn"
            />
        </ButtonRow>
    );
    return (
        <>
            <FilterLabel
                handelClearAllFilters={handelClearAllFilters}
                filtersCount={totalFilters}
            />
            <EuiFlexGroup
                className="step-container"
                gutterSize="s"
            >
                <EuiFlexItem>
                    <SelectWithSearch
                        placeholder="Nationality"
                        name="nationality"
                        customBtns={
                            <ActionBtns
                                name="nationality"
                                id={1}
                            />
                        }
                        isEditable
                        value={filters?.nationality?.value}
                        isMulti
                        handleChange={(value) => handleChangedData(value, "nationality", 1)}
                        handleFilter={(filter) =>
                            constantsCustomSearchFilter(NATIONALITIES, filter)
                        }
                        options={NATIONALITIES}
                    />
                </EuiFlexItem>
                <EuiFlexItem>
                    <SelectWithSearch
                        placeholder="Working Governerate"
                        name="workingGovernorates"
                        isEditable
                        customBtns={
                            <ActionBtns
                                name="workingGovernorates"
                                id={2}
                            />
                        }
                        isMulti
                        value={filters?.workingGovernorates?.value}
                        options={governorates}
                        handleChange={(value) => {
                            handleChangedData(value, "workingGovernorates", 2);
                            clearGovernoratesFilter();
                        }}
                        handleFilter={(filter) => {
                            setGovernoratesFilter(filter);
                            return governorates;
                        }}
                        onMenuClose={() => clearGovernoratesFilter()}
                    />
                </EuiFlexItem>
                <EuiFlexItem>
                    <SelectWithSearch
                        placeholder="Supplier Type"
                        name="supplierType"
                        customBtns={
                            <ActionBtns
                                name="supplierType"
                                id={1}
                            />
                        }
                        isEditable
                        value={filters?.supplierType?.value}
                        isMulti
                        handleChange={(value) => handleChangedData(value, "supplierType", 1)}
                        handleFilter={(filter) =>
                            constantsCustomSearchFilter(SUPPLIER_TYPES, filter)
                        }
                        options={SUPPLIER_TYPES}
                    />
                </EuiFlexItem>
                <EuiFlexItem>
                    <SelectWithSearch
                        placeholder="Payment Method"
                        name="paymentMethodType"
                        isEditable
                        customBtns={
                            <ActionBtns
                                name="paymentMethodType"
                                id={5}
                            />
                        }
                        isMulti
                        value={filters?.paymentMethodType?.value}
                        handleChange={(value) => handleChangedData(value, "paymentMethodType", 5)}
                        handleFilter={(filter) =>
                            constantsCustomSearchFilter(PAYMENT_METHODS, filter)
                        }
                        options={PAYMENT_METHODS}
                    />
                </EuiFlexItem>
                <EuiFlexItem>
                    <SelectWithSearch
                        placeholder="Status"
                        name="qaStatus"
                        isEditable
                        customBtns={
                            <ActionBtns
                                name="qaStatus"
                                id={6}
                            />
                        }
                        isMulti
                        value={filters?.qaStatus?.value}
                        handleChange={(value) => handleChangedData(value, "qaStatus", 6)}
                        handleFilter={(filter) => constantsCustomSearchFilter(QA_STATUS, filter)}
                        options={QA_STATUS}
                    />
                </EuiFlexItem>
                <EuiFlexItem>
                    <SelectWithSearch
                        placeholder="Platform"
                        name="source"
                        isEditable
                        customBtns={
                            <ActionBtns
                                name="source"
                                id={6}
                            />
                        }
                        isMulti
                        value={filters?.source?.value}
                        handleChange={(value) => handleChangedData(value, "source", 6)}
                        handleFilter={(filter) => constantsCustomSearchFilter(PLATFORMS, filter)}
                        options={PLATFORMS}
                    />
                </EuiFlexItem>
                <EuiFlexItem>
                    <SelectWithSearch
                        placeholder="License Type"
                        name="licenseType"
                        isEditable
                        customBtns={
                            <ActionBtns
                                name="licenseType"
                                id={7}
                            />
                        }
                        isMulti
                        value={filters?.licenseType?.value}
                        handleChange={(value) => handleChangedData(value, "licenseType", 7)}
                        handleFilter={(filter) => constantsCustomSearchFilter(LICENSE, filter)}
                        options={LICENSE}
                    />
                </EuiFlexItem>
            </EuiFlexGroup>
        </>
    );
};

export default DriversFilters;
